import ExcelJS from "exceljs";
import {
  buildMedicalFortuneSheetData,
  downloadMedicalTablePdf,
  ensureExtension,
  PDF_UNICODE_FONT_NAME,
  triggerBlobDownload,
} from "./medicalSheetCommon";
import { getSiteDisplayName } from "../constants/siteDisplayNames";
import { isAuditRosterCustomer } from "./auditSheetExport";
import { formatTbCustomerName } from "./tbSheetExport";
import {
  YES_NO_DROPDOWN,
  compareMedicalFormCustomersByName,
  displayYesNoValue,
  normalizeSheetCellText,
  yesNoPayload,
} from "./medicalCustomerSheetSave";

export const ROUNDING_HEADERS = [
  "No.",
  "Customer",
  "Eyes-On",
  "Assist seating/Hand over to family",
  "Airtag",
  "Rounding Staff",
  "9-10:30",
  "10:30-11:30",
  "11:30-12:30",
  "Rounding Notes",
];

const BASE_COL_WIDTHS_PX = [40, 220, 64, 110, 56, 80, 64, 72, 72, 360];
const BORDER_80 = "FF333333";
const LINE_COLOR = [51, 51, 51];
const HEADER_FILL = [217, 217, 217];
const EYES_ON_YES_FILL = [242, 242, 242];
const NOTES_COL = 9;
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SAFETY_NOTE =
  "please make sure there is no Clustter, cords in the pathway; no Loose carpet, slippery surfaces; no Unstable Furniture; no Lack of adequate lighting";

const border80 = {
  top: { style: "thin", color: { argb: BORDER_80 } },
  left: { style: "thin", color: { argb: BORDER_80 } },
  bottom: { style: "thin", color: { argb: BORDER_80 } },
  right: { style: "thin", color: { argb: BORDER_80 } },
};

/** Full Name (Preferred Name); omit parentheses when preferred is missing. */
export function formatRoundingCustomerName(customer) {
  return formatTbCustomerName(customer);
}

function displayYesNo(value) {
  return displayYesNoValue(value);
}

function eyesOnFromCustomer(customer) {
  const fromField = displayYesNo(customer?.eyes_on);
  if (fromField) return fromField;
  if (customer?.disability === true) return "Yes";
  return displayYesNo(customer?.disability);
}

function formatExportedOnDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const valid = Number.isNaN(d.getTime()) ? new Date() : d;
  return `${MONTHS[valid.getMonth()]} ${String(valid.getDate()).padStart(2, "0")}, ${valid.getFullYear()}`;
}

export function buildRoundingTitle(siteNum, exportedAt = new Date()) {
  const site = getSiteDisplayName(siteNum);
  return `${site} Rounding List & Restricted Outing List (Exported on ${formatExportedOnDate(exportedAt)})`;
}

export function isRoundingRosterCustomer(customer) {
  if (!isAuditRosterCustomer(customer)) return false;
  return displayYesNo(customer?.requires_rounding) === "Yes";
}

function customerIdOf(customerOrRow) {
  return `${customerOrRow?._id ?? customerOrRow?.id ?? customerOrRow?.customer_id ?? ""}`.trim();
}

function cellText(value) {
  return `${value ?? ""}`.replace(/\s+/g, " ").trim();
}

export function buildRoundingRows(customers = []) {
  const eligible = (customers || []).filter(isRoundingRosterCustomer);
  eligible.sort((a, b) => compareMedicalFormCustomersByName(a, b));
  return eligible.map((customer, index) => ({
    id: customerIdOf(customer),
    customer_id: customerIdOf(customer),
    no: index + 1,
    name: formatRoundingCustomerName(customer),
    eyesOn: eyesOnFromCustomer(customer),
    assistSeating: "",
    airtag: displayYesNo(customer?.has_airtag),
    roundingStaff: "",
    slot1: "",
    slot2: "",
    slot3: "",
    notes: cellText(customer?.rounding_notes),
  }));
}

export function roundingRowsFromApi(rows = []) {
  return (rows || []).map((row, index) => ({
    id: customerIdOf(row),
    customer_id: customerIdOf(row),
    no: Number(row?.no) || index + 1,
    name: cellText(row?.name),
    eyesOn: displayYesNo(row?.eyesOn) || cellText(row?.eyesOn),
    assistSeating: cellText(row?.assistSeating),
    airtag: displayYesNo(row?.airtag) || cellText(row?.airtag),
    roundingStaff: cellText(row?.roundingStaff),
    slot1: cellText(row?.slot1),
    slot2: cellText(row?.slot2),
    slot3: cellText(row?.slot3),
    notes: cellText(row?.notes),
  }));
}

function rowToCells(row) {
  return [
    `${row.no ?? ""}`,
    `${row.name ?? ""}`,
    `${row.eyesOn ?? ""}`,
    `${row.assistSeating ?? ""}`,
    `${row.airtag ?? ""}`,
    `${row.roundingStaff ?? ""}`,
    `${row.slot1 ?? ""}`,
    `${row.slot2 ?? ""}`,
    `${row.slot3 ?? ""}`,
    `${row.notes ?? ""}`,
  ];
}

export function buildRoundingSheetModel(rows, siteNum, chartDate = new Date()) {
  return {
    title: buildRoundingTitle(siteNum, chartDate),
    headers: ROUNDING_HEADERS,
    rows: (rows || []).map(rowToCells),
    columnWidthsPx: BASE_COL_WIDTHS_PX,
    name: "Rounding",
    headerRowHeightPx: 52,
    dataVerificationByColumn: {
      2: YES_NO_DROPDOWN,
      4: YES_NO_DROPDOWN,
    },
    halignByColumn: {
      0: 0,
      1: 1,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
      8: 0,
      9: 1,
    },
  };
}

function pushMergedRow(sheet, r, segments, heightPx, { fontSize = 11, bold = false, ht = 1 } = {}) {
  const celldata = sheet.celldata;
  const merge = sheet.config.merge;
  const rowlen = sheet.config.rowlen;
  const customHeight = sheet.config.customHeight;
  const columnCount = ROUNDING_HEADERS.length;
  const occupied = new Set();
  segments.forEach((seg) => {
    const text = `${seg.text ?? ""}`;
    const span = seg.span || 1;
    const start = seg.start;
    const align = seg.ht != null ? seg.ht : ht;
    celldata.push({
      r,
      c: start,
      v: {
        v: text,
        m: text,
        ct: { fa: "@", t: "s" },
        ff: "Arial",
        fs: fontSize,
        bl: bold ? 1 : 0,
        ht: align,
        vt: 0,
        tb: "2",
        bg: "#FFFFFF",
        mc: { r, c: start, rs: 1, cs: span },
      },
    });
    merge[`${r}_${start}`] = { r, c: start, rs: 1, cs: span };
    occupied.add(start);
    for (let c = start + 1; c < start + span; c += 1) {
      celldata.push({
        r,
        c,
        v: {
          ct: { fa: "@", t: "s" },
          bg: "#FFFFFF",
          mc: { r, c: start, rs: 1, cs: span },
        },
      });
      occupied.add(c);
    }
  });
  for (let c = 0; c < columnCount; c += 1) {
    if (occupied.has(c)) continue;
    celldata.push({
      r,
      c,
      v: { ct: { fa: "@", t: "s" }, bg: "#FFFFFF" },
    });
  }
  rowlen[`${r}`] = heightPx;
  customHeight[`${r}`] = 1;
}

function appendRoundingFooter(sheet, dataRowCount) {
  if (!sheet?.config) return;
  if (!sheet.config.merge) sheet.config.merge = {};
  if (!sheet.config.rowlen) sheet.config.rowlen = {};
  if (!sheet.config.customHeight) sheet.config.customHeight = {};
  const firstFooter = dataRowCount + 2;
  pushMergedRow(
    sheet,
    firstFooter,
    [{ start: 0, span: ROUNDING_HEADERS.length, text: SAFETY_NOTE, ht: 0 }],
    48,
    { fontSize: 10, ht: 0 }
  );
  pushMergedRow(
    sheet,
    firstFooter + 1,
    [
      { start: 0, span: 3, text: "Rounding 1" },
      { start: 3, span: 3, text: "Rounding 2" },
      { start: 6, span: 3, text: "Rounding 3" },
      { start: 9, span: 1, text: "Rounding 4" },
    ],
    28,
    { fontSize: 11, ht: 1 }
  );
  pushMergedRow(
    sheet,
    firstFooter + 2,
    [
      { start: 0, span: 3, text: "Print Name:" },
      { start: 3, span: 3, text: "Print Name:" },
      { start: 6, span: 3, text: "Print Name:" },
      { start: 9, span: 1, text: "Print Name:" },
    ],
    28,
    { fontSize: 11, ht: 1 }
  );
  pushMergedRow(
    sheet,
    firstFooter + 3,
    [
      { start: 0, span: 3, text: "Signature:" },
      { start: 3, span: 3, text: "Signature:" },
      { start: 6, span: 3, text: "Signature:" },
      { start: 9, span: 1, text: "Signature:" },
    ],
    28,
    { fontSize: 11, ht: 1 }
  );
  sheet.row = Math.max(sheet.row || 0, firstFooter + 8);
}

export function buildRoundingFortuneSheetData(rows, siteNum, chartDate = new Date()) {
  const sheets = buildMedicalFortuneSheetData(buildRoundingSheetModel(rows, siteNum, chartDate));
  if (sheets[0]) appendRoundingFooter(sheets[0], (rows || []).length);
  return sheets;
}

export function parseRoundingRowsFromWorkbook(workbook, baselineRows = []) {
  const sheetRows = workbook?.[0]?.data;
  if (!Array.isArray(sheetRows)) return baselineRows;
  return (baselineRows || []).map((baseline, index) => {
    const row = sheetRows[index + 2] || [];
    return {
      ...baseline,
      eyesOn: displayYesNo(normalizeSheetCellText(row[2])) || normalizeSheetCellText(row[2]),
      assistSeating: normalizeSheetCellText(row[3]),
      airtag: displayYesNo(normalizeSheetCellText(row[4])) || normalizeSheetCellText(row[4]),
      roundingStaff: normalizeSheetCellText(row[5]),
      slot1: normalizeSheetCellText(row[6]),
      slot2: normalizeSheetCellText(row[7]),
      slot3: normalizeSheetCellText(row[8]),
      notes: normalizeSheetCellText(row[9]),
    };
  });
}

const ROUNDING_COMPARE_KEYS = [
  "id",
  "eyesOn",
  "assistSeating",
  "airtag",
  "roundingStaff",
  "slot1",
  "slot2",
  "slot3",
  "notes",
];

export function roundingRowsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  return a.every((row, i) => {
    const other = b[i] || {};
    return ROUNDING_COMPARE_KEYS.every((key) => cellText(row?.[key]) === cellText(other?.[key]));
  });
}

export function collectRoundingCustomerUpdates(draftRows = [], baselineRows = []) {
  const byId = new Map((baselineRows || []).map((row) => [customerIdOf(row), row]));
  const updates = [];
  (draftRows || []).forEach((row) => {
    const id = customerIdOf(row);
    const prev = byId.get(id);
    if (!id || !prev) return;
    const fields = {};
    const nextEyes = yesNoPayload(row.eyesOn);
    const prevEyes = yesNoPayload(prev.eyesOn);
    if (nextEyes !== prevEyes) {
      fields.eyes_on = nextEyes;
      fields.disability = nextEyes === "yes";
    }
    const nextAirtag = yesNoPayload(row.airtag);
    const prevAirtag = yesNoPayload(prev.airtag);
    if (nextAirtag !== prevAirtag) fields.has_airtag = nextAirtag;
    const nextNotes = cellText(row.notes);
    const prevNotes = cellText(prev.notes);
    if (nextNotes !== prevNotes) fields.rounding_notes = nextNotes;
    if (Object.keys(fields).length) updates.push({ customerId: id, ...fields });
  });
  return updates;
}

function drawRoundingPdfFooter(doc, ctx) {
  const { marginX, pageWidth, pageHeight, marginY, footerHeight, footerTop } = ctx;
  const tableWidth = pageWidth - marginX * 2;
  const safetyHeight = 28;
  const blockHeight = 18;
  const signRowHeight = 27;
  let y =
    Number.isFinite(footerTop) && footerTop > 0
      ? footerTop
      : pageHeight - marginY - footerHeight + 4;

  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.5);
  doc.setFont(PDF_UNICODE_FONT_NAME, "normal");
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  doc.rect(marginX, y, tableWidth, safetyHeight);
  const safetyLines = doc.splitTextToSize(SAFETY_NOTE, tableWidth - 8);
  doc.text(safetyLines, pageWidth / 2, y + 10, { align: "center" });
  y += safetyHeight;

  const blocks = [
    { label: "Rounding 1", width: tableWidth * 0.27 },
    { label: "Rounding 2", width: tableWidth * 0.27 },
    { label: "Rounding 3", width: tableWidth * 0.28 },
    { label: "Rounding 4", width: tableWidth * 0.18 },
  ];
  let x = marginX;
  blocks.forEach((block) => {
    doc.rect(x, y, block.width, blockHeight);
    doc.setFontSize(8);
    doc.text(block.label, x + 4, y + 12);
    x += block.width;
  });
  y += blockHeight;

  x = marginX;
  blocks.forEach((block) => {
    doc.rect(x, y, block.width, signRowHeight);
    doc.text("Print Name:", x + 4, y + 12);
    x += block.width;
  });
  y += signRowHeight;

  x = marginX;
  blocks.forEach((block) => {
    doc.rect(x, y, block.width, signRowHeight);
    doc.text("Signature:", x + 4, y + 12);
    x += block.width;
  });
}

/** Last, First on line 1; preferred name on line 2 (blank line if none). */
function roundingCustomerNameForPdf(name) {
  const text = `${name ?? ""}`.trim();
  const match = text.match(/^(.*?)(?:\s*\(([^()]*)\))\s*$/);
  const line1 = (match ? match[1] : text).trim();
  const line2 = `${match?.[2] ?? ""}`.trim();
  return `${line1}\n${line2 || " "}`;
}

function applyRoundingPdfRowFill(hookData) {
  if (hookData.section !== "body") return;
  const isGray = (hookData.row?.index ?? 0) % 2 === 1;
  hookData.cell.styles.fillColor = isGray ? EYES_ON_YES_FILL : [255, 255, 255];
}

export async function downloadRoundingPdf(rows, siteNum, filename = "Rounding_Chart", chartDate = new Date()) {
  const model = buildRoundingSheetModel(rows, siteNum, chartDate);
  const pdfRows = (model.rows || []).map((cells) => {
    const next = [...cells];
    next[1] = roundingCustomerNameForPdf(next[1]);
    return next;
  });
  await downloadMedicalTablePdf({
    title: model.title,
    headers: model.headers,
    rows: pdfRows,
    filename,
    pageOptions: {
      orientation: "landscape",
      fontSize: 10,
      headFontSize: 10,
      titleFontSize: 16,
      cellPadding: 2,
      headerHeight: 44,
      marginX: 12,
      marginY: 12,
      footerHeight: 106,
      footerOnLastPageOnly: true,
      fillTableWidth: true,
      fillFromIndex: NOTES_COL,
      lineColor: LINE_COLOR,
      headFillColor: HEADER_FILL,
      didParseCell: applyRoundingPdfRowFill,
      drawFooter: drawRoundingPdfFooter,
    },
    columnStyles: {
      0: { cellWidth: 28, halign: "center" },
      1: { cellWidth: 140, halign: "left" },
      2: { cellWidth: 50, halign: "center" },
      3: { cellWidth: 68, halign: "center" },
      4: { cellWidth: 42, halign: "center" },
      5: { cellWidth: 60, halign: "center" },
      6: { cellWidth: 42, halign: "center" },
      7: { cellWidth: 46, halign: "center" },
      8: { cellWidth: 46, halign: "center" },
      9: { cellWidth: 220, halign: "left" },
    },
  });
}

function applyExcelBorders(sheet, rowNumber, colCount) {
  const row = sheet.getRow(rowNumber);
  for (let col = 1; col <= colCount; col += 1) {
    row.getCell(col).border = border80;
  }
}

export async function downloadRoundingXlsx(rows, siteNum, filename = "Rounding_Chart", chartDate = new Date()) {
  const model = buildRoundingSheetModel(rows, siteNum, chartDate);
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Worldshine";
  const sheet = workbook.addWorksheet("Rounding");
  const colCount = model.headers.length;

  sheet.mergeCells(1, 1, 1, colCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = model.title;
  titleCell.font = { bold: true, size: 16, name: "Times New Roman" };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  applyExcelBorders(sheet, 1, colCount);
  sheet.getRow(1).height = 28;

  const headerRow = sheet.getRow(2);
  headerRow.height = 51;
  model.headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { size: 10, name: "Arial" };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = border80;
  });

  const dataStart = 3;
  model.rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(dataStart + rowIndex);
    excelRow.height = 27;
    row.forEach((value, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      cell.value = value || null;
      cell.font = { size: 11, name: "Arial" };
      cell.alignment = {
        horizontal: colIndex === 1 || colIndex === NOTES_COL ? "left" : "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = border80;
    });
  });

  const footerStart = dataStart + Math.max(model.rows.length, 0);
  const safetyRow = footerStart;
  sheet.mergeCells(safetyRow, 1, safetyRow, colCount);
  const safetyCell = sheet.getCell(safetyRow, 1);
  safetyCell.value = SAFETY_NOTE;
  safetyCell.font = { size: 11, name: "Calibri" };
  safetyCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  applyExcelBorders(sheet, safetyRow, colCount);
  sheet.getRow(safetyRow).height = 28;

  const labelRow = safetyRow + 1;
  const nameRow = safetyRow + 2;
  const signRow = safetyRow + 3;
  const footerMerges = [
    { start: 1, end: 3, label: "Rounding 1" },
    { start: 4, end: 6, label: "Rounding 2" },
    { start: 7, end: 9, label: "Rounding 3" },
    { start: 10, end: 10, label: "Rounding 4" },
  ];
  [labelRow, nameRow, signRow].forEach((r) => {
    sheet.getRow(r).height = 27;
  });
  footerMerges.forEach((block) => {
    if (block.end > block.start) {
      sheet.mergeCells(labelRow, block.start, labelRow, block.end);
      sheet.mergeCells(nameRow, block.start, nameRow, block.end);
      sheet.mergeCells(signRow, block.start, signRow, block.end);
    }
    const labelCell = sheet.getCell(labelRow, block.start);
    labelCell.value = block.label;
    labelCell.font = { size: 11, name: "Calibri" };
    labelCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    const nameCell = sheet.getCell(nameRow, block.start);
    nameCell.value = "Print Name:";
    nameCell.font = { size: 11, name: "Calibri" };
    nameCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    const signCell = sheet.getCell(signRow, block.start);
    signCell.value = "Signature:";
    signCell.font = { size: 11, name: "Calibri" };
    signCell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  });
  applyExcelBorders(sheet, labelRow, colCount);
  applyExcelBorders(sheet, nameRow, colCount);
  applyExcelBorders(sheet, signRow, colCount);

  const excelColWidths = [4.2, 28, 8.3, 12.9, 5.9, 8.6, 7.7, 7.7, 7.7, 48];
  excelColWidths.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });

  sheet.pageSetup = {
    paperSize: 1,
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    horizontalCentered: true,
    margins: { left: 0.25, right: 0.25, top: 0.25, bottom: 0.25, header: 0.2, footer: 0.2 },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ensureExtension(filename, ".xlsx")
  );
}

